import {
	ChainInfo,
	Header as SymbiosisHeader,
	HeaderHandler as SymbiosisHeaderHandler,
	HeaderSubMenuItem,
	HeaderWallet,
} from 'symbiosis-uikit'

import styles from './Header.pcss'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useWallet } from '~/lib/useWallet'
import {
	CONFIG,
	CROSSCHAIN_CONFIG,
	getChainById,
	getChainByIdFromSDK,
} from '~/lib/config'
import { useGetBalance } from '~/lib/useGetBalance'
import { ethers } from 'ethers'
import { useGetSisBalance } from '~/lib/useGetSisBalance'
import HeaderTabs from './HeaderTabs'
import { ConnectWalletModal } from '~/components/ConnectWalletModal'

function Header(): JSX.Element {
	const header = useRef<SymbiosisHeaderHandler>(null)
	const { pathname } = useLocation()
	const { setChain } = useWallet()

	const [showConnectWalletModal, setShowConnectWalletModal] = useState(false)

	// Close header on route change
	useEffect(() => {
		header.current?.close()
	}, [pathname])

	const isLPFarm = useMemo(() => pathname.startsWith('/lp-farm'), [pathname])

	const { account, chainId, disconnect } = useWallet()

	useLayoutEffect(() => {
		if (!account) {
			return
		}

		setShowConnectWalletModal(false)
	}, [account])

	const balance = useGetBalance()
	const sisBalance = useGetSisBalance()

	const availableChains = useMemo((): ChainInfo[] => {
		// @@
		const stakeChains = CONFIG.chains
		const crosschainFarmChains = CROSSCHAIN_CONFIG.chains.all.filter(
			(item) =>
				!!(
					CROSSCHAIN_CONFIG.chains.enabled.includes(item.id) &&
					!stakeChains.find((stakeChain) => stakeChain.id === item.id)
				)
		)

		return [...stakeChains, ...crosschainFarmChains]
			.map((chain) => {
				const chainSDK = getChainByIdFromSDK(chain.id, isLPFarm)
				if (!chainSDK) return null
				return {
					id: chainSDK!.id,
					name: chainSDK!.name,
					image: chainSDK!.icons.large,
				} as ChainInfo
			})
			.filter((chain) => !!chain)
	}, [isLPFarm])

	let wallet: HeaderWallet
	let onWalletClick: (() => void) | undefined
	if (!account) {
		onWalletClick = () => {
			header.current?.close()
			setShowConnectWalletModal(true)
		}
	} else {
		const chain = getChainById(chainId)
		const scanUrl = chain?.scanUrl
		const symbol = chain?.token?.symbol ?? ''

		wallet = {
			chainId: chainId,
			chains: availableChains,
			address: account,
			explorerUrl: scanUrl && `${scanUrl}/address/${account}`,
			balance: balance
				? {
						amount: ethers.utils.formatUnits(balance, 'ether'),
						symbol,
				  }
				: undefined,
			sisBalance: sisBalance
				? {
						amount: ethers.utils.formatUnits(sisBalance, 'ether'),
						symbol: 'SIS',
				  }
				: undefined,
		}
	}

	return (
		<>
			<SymbiosisHeader
				className={styles.header}
				ref={header}
				activeItem="rewards"
				wallet={wallet}
				onWalletClick={onWalletClick}
				onLogoutClick={disconnect}
				onChainIdChange={setChain}
				middleContent={<HeaderTabs />}
				subMenuItems={
					<>
						<HeaderSubMenuItem as={Link} to="/crosschain-farm-v2">
							Cross-chain Farming
						</HeaderSubMenuItem>
						<HeaderSubMenuItem as={Link} to="/crosschain-farm">
							Cross-chain Farming V1
						</HeaderSubMenuItem>
						<HeaderSubMenuItem
							as={Link}
							to="/lp-farm/:earnTypePath/:page"
						>
							LP Farming
						</HeaderSubMenuItem>
					</>
				}
			/>
			<ConnectWalletModal
				show={showConnectWalletModal}
				onDismiss={() => {
					setShowConnectWalletModal(false)
				}}
			/>
		</>
	)
}

export default Header
